import React, { Children } from 'react';
import { motion } from 'framer-motion';
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function PieChart({
  label,
  gpuPercent,
  fpgaPercent,
  year
}: {
  label: string;
  gpuPercent: number;
  fpgaPercent: number;
  year: string;
}) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const gpuDash = gpuPercent / 100 * circumference;
  const fpgaDash = fpgaPercent / 100 * circumference;
  return <div className="flex flex-col items-center" data-id="element-217">
      <svg width="110" height="110" viewBox="0 0 110 110" data-id="element-218">
        <circle cx="55" cy="55" r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="12" data-id="element-219" />
        {/* GPU slice */}
        <circle cx="55" cy="55" r={radius} fill="none" stroke="#ff6b35" strokeWidth="12" strokeDasharray={`${gpuDash} ${circumference}`} strokeDashoffset="0" transform="rotate(-90 55 55)" strokeLinecap="round" data-id="element-220" />
        {/* FPGA slice */}
        <circle cx="55" cy="55" r={radius} fill="none" stroke="#00d4ff" strokeWidth="12" strokeDasharray={`${fpgaDash} ${circumference}`} strokeDashoffset={`-${gpuDash}`} transform="rotate(-90 55 55)" strokeLinecap="round" data-id="element-221" />
        <text x="55" y="52" textAnchor="middle" className="fill-white text-sm font-bold" data-id="element-222">
          {year}
        </text>
        <text x="55" y="67" textAnchor="middle" className="fill-[#8899aa] text-[9px]" data-id="element-223">
          {label}
        </text>
      </svg>
      <div className="mt-3 space-y-1 text-base" data-id="element-224">
        <div className="flex items-center gap-2" data-id="element-225">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff6b35]" data-id="element-226" />
          <span className="text-[#8899aa]" data-id="element-227">GPU {gpuPercent}%</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-228">
          <span className="w-2.5 h-2.5 rounded-full bg-[#00d4ff]" data-id="element-229" />
          <span className="text-[#8899aa]" data-id="element-230">FPGA/ASIC {fpgaPercent}%</span>
        </div>
      </div>
    </div>;
}
export function ProblemSlide() {
  return <motion.div className="w-full h-full flex flex-col px-12 py-10" variants={stagger} initial="hidden" animate="visible" data-id="element-231">
      {/* Slide label */}
      <motion.div variants={fadeUp} className="flex items-center gap-3 mb-2" data-id="element-232">
        <span className="text-base text-[#ff6b35] font-semibold tracking-widest uppercase" data-id="element-233">
          02
        </span>
        <span className="w-8 h-[1px] bg-[#ff6b35] opacity-50" data-id="element-234" />
      </motion.div>

      <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-extrabold text-white mb-8" data-id="element-235">
        Problem
      </motion.h2>

      <div className="flex-1 flex flex-col md:flex-row gap-8" data-id="element-236">
        {/* Bullets */}
        <div className="flex-1 space-y-5" data-id="element-237">
          <motion.div variants={fadeUp} className="glass-card p-5" data-id="element-238">
            <p className="text-[20px] text-white/90 leading-relaxed" data-id="element-239">
              GPUs won the first decade of AI by availability, not by
              architectural merit.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="glass-card p-5" data-id="element-240">
            <p className="text-[20px] text-white/90 leading-relaxed" data-id="element-241">
              FPGAs can be 10–50x more efficient, but tools are complex and
              deployment is a nightmare
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="glass-card p-5 border-l-2 border-l-[#00d4ff]" data-id="element-242">
            <p className="text-[20px] text-white/90 leading-relaxed" data-id="element-243">
              Goldman Sachs predicts up to{' '}
              <span className="text-[#00d4ff] font-semibold" data-id="element-244">
                50% of AI will run on FPGAs and ASICs in 2027
              </span>
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="glass-card p-5" data-id="element-245">
            <p className="text-[20px] text-white/90 leading-relaxed" data-id="element-246">
              No cloud-native way to run FPGA inference—every team builds a
              custom pipeline
            </p>
          </motion.div>
        </div>

        {/* Pie charts */}
        <motion.div variants={fadeUp} className="flex flex-row md:flex-col items-center justify-center gap-8 md:w-56" data-id="element-247">
          <PieChart label="Today" gpuPercent={95} fpgaPercent={5} year="2026" data-id="element-248" />
          {/* Arrow */}
          <div className="hidden md:flex flex-col items-center gap-1" data-id="element-249">
            <div className="w-[1px] h-6 bg-[#00d4ff] opacity-30" data-id="element-250" />
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" data-id="element-251">
              <path d="M1 1L6 6L11 1" stroke="#00d4ff" strokeWidth="1.5" strokeLinecap="round" data-id="element-252" />
            </svg>
          </div>
          <PieChart label="Predicted" gpuPercent={50} fpgaPercent={50} year="2027" data-id="element-253" />
        </motion.div>
      </div>

      <motion.p variants={fadeUp} className="text-base text-[#8899aa] mt-4 opacity-60" data-id="element-254">
        Source: Chang, Allen, et al. Global Server: ASIC Servers Expanding; AI
        Full Racks See Diversifying Chip Platform. Goldman Sachs Global
        Investment Research, 4 Jan. 2026.
      </motion.p>
    </motion.div>;
}